/**
 * Page version history (L4 — Google Docs style).
 *
 * Sheet with two views:
 *   · List: local versions (PowerSync bucket, latest 10 offline) +
 *     deep history on demand via paginated API.
 *   · Detail: word-by-word diff against the CURRENT text (title +
 *     plain text of the blocks) + full version text +
 *     "Restore this version" (normal forward write: the current state
 *     is archived as a new version — history is never rewritten).
 */
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, History, Loader2, RotateCcw } from "@fleet/icons";
import { cn, type components } from "@apunte-digital/shared";
import type { Block, PageVersion } from "@apunte-digital/powersync";
import { usePageVersionsQuery } from "@apunte-digital/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { WordDiff } from "@/components/ui/word-diff";
import { useI18n } from "@/providers/i18n-provider";
import { formatRelativeTime } from "../lib/format";

const PREVIEW_MAX_CHARS = 120;
const LOCAL_SYNC_WINDOW = 10;
const DEEP_PAGE_SIZE = 20;

interface VersionHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageId: () => string;
  /** Current page title (diff reference). */
  currentTitle: () => string;
  /** Current page blocks (diff reference). */
  currentBlocks: () => Block[];
  /** Restore: the caller performs the write and closes if appropriate. */
  onRestore: (version: PageVersion) => void;
  restoring?: () => boolean;
}

/** API row (camelCase, generated types) → local shape (snake_case). */
type ApiVersion = components["schemas"]["PageVersion"];

function fromApi(row: ApiVersion, userId: string | null): PageVersion {
  return {
    id: row.id,
    user_id: userId,
    page_id: row.pageId,
    title: row.title,
    icon: row.icon ?? null,
    blocks: JSON.stringify(row.blocks ?? []),
    text: row.text,
    client_ts: row.clientTs ?? null,
    server_ts: row.serverTs,
  };
}

function preview(text: string | null): string {
  const clean = (text ?? "").replaceAll(/\s+/gu, " ").trim();
  return clean.length > PREVIEW_MAX_CHARS ? `${clean.slice(0, PREVIEW_MAX_CHARS)}…` : clean;
}

export function VersionHistorySheet(props: VersionHistorySheetProps) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const versions = usePageVersionsQuery({
    db: () => db(),
    drizzle,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    pageId: () => props.pageId(),
  });

  /** Current plain text of the page (blocks in document order). */
  const currentText = createMemo(() =>
    props
      .currentBlocks()
      .map((row) => row.text ?? "")
      .join("\n"),
  );

  // Deep history (API): accumulated pages + cursor.
  const [deepVersions, setDeepVersions] = createSignal<PageVersion[]>([]);
  const [deepCursor, setDeepCursor] = createSignal<string | null>(null);
  const [deepExhausted, setDeepExhausted] = createSignal(false);
  const [deepLoading, setDeepLoading] = createSignal(false);
  const [selected, setSelected] = createSignal<PageVersion | null>(null);
  const [view, setView] = createSignal<"changes" | "content">("changes");

  const allVersions = createMemo<PageVersion[]>(() => {
    const local = versions.data ?? [];
    const seen = new Set(local.map((v) => v.id));
    return [...local, ...deepVersions().filter((v) => !seen.has(v.id))];
  });

  // We only offer "load more" if the local window is full (if there are fewer
  // than 10 local versions, the device already has ALL the history).
  const mayHaveDeeper = createMemo(
    () => (versions.data ?? []).length >= LOCAL_SYNC_WINDOW && !deepExhausted(),
  );

  const loadOlder = async () => {
    if (deepLoading()) {
      return;
    }
    setDeepLoading(true);
    try {
      const cursor = deepCursor() ?? allVersions().at(-1)?.id ?? undefined;
      const { data, error } = await api.GET("/api/notes/page/{id}/versions", {
        params: { path: { id: props.pageId() }, query: { cursor, limit: DEEP_PAGE_SIZE } },
      });
      if (error || !data) {
        return;
      }
      const uid = userId() ?? null;
      setDeepVersions((prev) => [...prev, ...data.versions.map((v) => fromApi(v, uid))]);
      setDeepCursor(data.nextCursor ?? null);
      if (!data.nextCursor) {
        setDeepExhausted(true);
      }
    } catch (error) {
      console.error("Error loading older versions:", error);
    } finally {
      setDeepLoading(false);
    }
  };

  // On close (by the user OR programmatically after restore), the
  // next opening always starts in the LIST, never in a stale detail.
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (!open) {
          setSelected(null);
          setView("changes");
        }
      },
      { defer: true },
    ),
  );

  const close = (open: boolean) => {
    props.onOpenChange(open);
  };

  return (
    <Dialog open={props.open} onOpenChange={close} contentClass="max-w-lg">
      <div
        class="flex max-h-[70vh] min-h-[40vh] flex-col gap-3 p-1"
        data-testid="version-history-sheet"
      >
        <Show
          when={selected()}
          fallback={
            <>
              <div class="flex items-center gap-2">
                <History class="h-4 w-4 text-accent" />
                <DialogTitle>{t(msg`Version history`)}</DialogTitle>
              </div>
              <p class="text-xs text-muted-foreground">
                {t(msg`Your previous words are kept here as you edit this page.`)}
              </p>

              <div class="flex flex-col gap-2 overflow-y-auto" data-testid="version-list">
                <Show
                  when={!versions.isLoading}
                  fallback={
                    <div class="flex flex-col gap-2">
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                    </div>
                  }
                >
                  <Show
                    when={allVersions().length > 0}
                    fallback={
                      <EmptyState
                        icon={History}
                        title={t(msg`No versions yet`)}
                        description={t(
                          msg`Keep writing — earlier states of this page will be kept here.`,
                        )}
                        class="py-6"
                      />
                    }
                  >
                    <For each={allVersions()}>
                      {(version) => (
                        <button
                          type="button"
                          data-testid="version-item"
                          class="flex flex-col gap-1 rounded-xl border border-border/50 bg-muted/20 p-3 text-left transition-colors hover:border-accent/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                          onClick={() => setSelected(version)}
                        >
                          <div class="flex items-center justify-between gap-2">
                            <span class="text-xs font-semibold text-foreground">
                              {formatRelativeTime(version.client_ts ?? version.server_ts, locale())}
                            </span>
                            <Show when={version.icon}>
                              <span class="text-xs">{version.icon}</span>
                            </Show>
                          </div>
                          <Show when={version.title}>
                            <span class="truncate text-sm font-semibold text-foreground/90">
                              {version.title}
                            </span>
                          </Show>
                          <span class="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {preview(version.text)}
                          </span>
                        </button>
                      )}
                    </For>
                    <Show when={mayHaveDeeper()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-muted-foreground"
                        data-testid="version-load-more"
                        disabled={deepLoading()}
                        onClick={() => {
                          void loadOlder();
                        }}
                      >
                        <Show when={deepLoading()}>
                          <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        </Show>
                        {t(msg`Load older versions`)}
                      </Button>
                    </Show>
                  </Show>
                </Show>
              </div>
            </>
          }
        >
          {(version) => (
            <>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(msg`Back to version list`)}
                  data-testid="version-back"
                  onClick={() => setSelected(null)}
                >
                  <ArrowLeft class="h-4 w-4" />
                </button>
                <DialogTitle class="flex-1 truncate">
                  {formatRelativeTime(version().client_ts ?? version().server_ts, locale())}
                </DialogTitle>
                <div class="flex items-center rounded-lg bg-muted/40 p-0.5 text-xs">
                  <button
                    type="button"
                    data-testid="version-view-changes"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "changes" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("changes")}
                  >
                    {t(msg`Changes`)}
                  </button>
                  <button
                    type="button"
                    data-testid="version-view-content"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "content" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("content")}
                  >
                    {t(msg`Full text`)}
                  </button>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto rounded-xl border border-border/40 bg-muted/10 p-3">
                <Show when={version().title || props.currentTitle()}>
                  <div class="mb-2 border-b border-border/30 pb-2">
                    <Show
                      when={view() === "changes"}
                      fallback={<span class="text-base font-semibold">{version().title}</span>}
                    >
                      <WordDiff
                        oldText={version().title ?? ""}
                        newText={props.currentTitle()}
                        class="text-base font-semibold"
                      />
                    </Show>
                  </div>
                </Show>
                <Show
                  when={view() === "changes"}
                  fallback={
                    <p
                      class="whitespace-pre-wrap break-words text-sm leading-[1.75] text-foreground"
                      data-testid="version-content"
                    >
                      {version().text}
                    </p>
                  }
                >
                  <WordDiff oldText={version().text ?? ""} newText={currentText()} />
                </Show>
              </div>

              <p class="text-[11px] leading-relaxed text-muted-foreground/70">
                {t(
                  msg`Restoring never deletes history: what you have now is saved as a new version first.`,
                )}
              </p>
              <div class="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => close(false)}>
                  {t(msg`Cancel`)}
                </Button>
                <Button
                  size="sm"
                  data-testid="version-restore"
                  disabled={props.restoring?.() === true}
                  onClick={() => {
                    props.onRestore(version());
                  }}
                >
                  <Show
                    when={props.restoring?.() === true}
                    fallback={<RotateCcw class="mr-1.5 h-3.5 w-3.5" />}
                  >
                    <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  </Show>
                  {t(msg`Restore this version`)}
                </Button>
              </div>
            </>
          )}
        </Show>
      </div>
    </Dialog>
  );
}
